/**
 * A template's short code, in the shape the backend actually accepts.
 *
 * `template.create` validates `code` against `^[a-z0-9][a-z0-9-]{1,63}$` — lower case, two to
 * sixty-four characters, starting on a letter or a digit. The wizard did not know that. It
 * UPPERCASED whatever it derived (`trUpper`), passed anything typed straight through, and cut the
 * result to ten characters without saying so; `CXO100-SK` therefore travelled as `CXO100-SK` and
 * came back refused — after all seven steps, as «code — biçimi geçersiz», with no field named on
 * the screen the author could still fix.
 *
 * So the rule lives here, once, next to the pattern it restates, and the wizard checks it on the
 * step that owns the field. Nothing is trimmed behind the author's back: `normalizeTemplateCode`
 * returns what it produced, the field is filled with that value, and a value that cannot be made
 * into a code at all is refused with a reason rather than quietly cut down to one.
 */
import { trLower } from './reference';

/** The backend's own pattern (`api/schemas.ts`), restated where the browser can check it first. */
export const TEMPLATE_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{1,63}$/;
export const TEMPLATE_CODE_MIN = 2;
export const TEMPLATE_CODE_MAX = 64;

/**
 * Turkish letters, folded to the ASCII the pattern allows.
 *
 * `ş` and `ğ` are not `[a-z]`, so a name like «Şube Lideri» has to become `sube-lideri` rather than
 * losing its first letter to the separator rule. `trLower` first, because `toLowerCase()` maps
 * `I` to `i` where Turkish wants `ı`.
 */
const FOLD: Record<string, string> = {
  ı: 'i',
  İ: 'i',
  ş: 's',
  Ş: 's',
  ğ: 'g',
  Ğ: 'g',
  ç: 'c',
  Ç: 'c',
  ö: 'o',
  Ö: 'o',
  ü: 'u',
  Ü: 'u',
  â: 'a',
  î: 'i',
  û: 'u',
};

/**
 * The code a value becomes — lower case, folded, separated by single hyphens.
 *
 * Returns the empty string when there is nothing usable left, which the caller reports rather than
 * papering over. A result is never longer than the column allows and never ends on a hyphen.
 */
export function normalizeTemplateCode(raw: string | null | undefined): string {
  const folded = trLower(String(raw ?? ''))
    .split('')
    .map((c) => FOLD[c] ?? c)
    .join('');
  return folded
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, TEMPLATE_CODE_MAX)
    .replace(/-+$/, '');
}

export type TemplateCodeProblem = 'empty' | 'too_short' | 'unusable';

/** What is wrong with a code, or null when nothing is. */
export function templateCodeProblem(code: string): TemplateCodeProblem | null {
  if (code.length === 0) return 'empty';
  if (code.length < TEMPLATE_CODE_MIN) return 'too_short';
  return TEMPLATE_CODE_PATTERN.test(code) ? null : 'unusable';
}

/** The sentence shown beside «Kısa kod» — it says what to do, not which regular expression failed. */
export function templateCodeMessage(problem: TemplateCodeProblem, typed: string): string {
  if (problem === 'empty')
    return typed.trim()
      ? 'Kısa kod yalnız harf, rakam ve tire içerebilir; girdiğiniz değerden kullanılabilir bir kod çıkmadı.'
      : 'Kısa kod gerekli.';
  if (problem === 'too_short') return 'Kısa kod en az 2 karakter olmalı.';
  return 'Kısa kod yalnız küçük harf, rakam ve tire içerebilir; harf veya rakamla başlamalı.';
}

export interface TemplateCodeCheck {
  /** The value that should be stored and shown in the field. */
  value: string;
  /** Null when the value is acceptable to the backend. */
  message: string | null;
}

/**
 * Normalises a typed code and says whether the result can be used.
 *
 * The normalised value comes back even when it is refused, so the field can show what was made of
 * the input instead of the author guessing why their own text was not accepted.
 */
export function checkTemplateCode(typed: string | null | undefined): TemplateCodeCheck {
  const value = normalizeTemplateCode(typed);
  const problem = templateCodeProblem(value);
  return { value, message: problem ? templateCodeMessage(problem, String(typed ?? '')) : null };
}

/**
 * A code derived from the template's name, for an author who left the field alone.
 *
 * The whole name rather than its first word: `sube-lideri-diplomasi` is a code somebody can
 * recognise in a list, and the pattern allows sixty-four characters. `sablon` is the last resort
 * for a name that folds to nothing at all — it is a real code, which «ŞBL» was not.
 */
export function templateCodeFromName(name: string): string {
  const derived = normalizeTemplateCode(name);
  return templateCodeProblem(derived) === null ? derived : 'sablon';
}
